/**
 * What a refused `/ai` request means for the screen (the backend's Problem Details codes, documented
 * on `AiController`): Buddy switched off, a usage window full, a turn already running, or a failure
 * the screen can only report.
 *
 * Pure: it reads the refusal's status, code and body, whatever threw them.
 */

import type { AiUsageMeter, AiUsageWindow } from './conversation';

export type AiRefusal =
  /** 403 `ai_unavailable`: kill switch, service ceiling, or not in the beta group (R3, R44). */
  | { readonly kind: 'unavailable' }
  /** 429 `ai_limit_reached`: the window that ran out and when it frees up (R39, R43). */
  | { readonly kind: 'limit'; readonly meter: AiUsageMeter }
  /** 429 `rate_limited`: too many requests at once (an import already running); try again shortly. */
  | { readonly kind: 'busy' }
  /** 409 `ai_turn_running`: the conversation moved (another window); read it again. */
  | { readonly kind: 'stale' }
  /** 409 `ai_outline_not_waiting`: the outline or build acted on is no longer the newest; read again. */
  | { readonly kind: 'notWaiting' }
  /** 400 `ai_answer_invalid`: the answers no longer match the questions Buddy is waiting on; read again. */
  | { readonly kind: 'answerInvalid' }
  /** 400 `ai_snapshot_invalid`: the draft a message carried is one a save would refuse (R13). */
  | { readonly kind: 'snapshotInvalid' }
  /** `ai_snapshot_too_large`: the draft a message carried is past what Buddy reads; `limit` the bound, when named. */
  | { readonly kind: 'snapshotTooLarge'; readonly limit: number | null }
  /** 400 `ai_decision_invalid`: the change named is not one of the proposal's; read again. */
  | { readonly kind: 'decisionInvalid' }
  /**
   * A spreadsheet refused (R26, R31), by why. `limit` is the bound it passed, as the server named it
   * (bytes for a size, a count for sheets, rows or cells); `null` when it named none.
   */
  | {
      readonly kind: 'import';
      readonly reason: AiImportRefusalReason;
      readonly limit: number | null;
      /** The chosen sheet past the bounds, when the server named one (`ai_import_sheet_too_large`). */
      readonly sheet: string | null;
    }
  /** A dictation refused by its own route (R58, R59): the recording, its language, or the speech provider. */
  | { readonly kind: 'dictation'; readonly reason: AiDictationRefusalReason }
  /** Anything else: the send did not happen, and the trainer's words stay in the composer. */
  | { readonly kind: 'failed' };

/**
 * Why a dictation was not turned into text: the backend's `ai_audio_*`, `ai_language_invalid` and
 * `ai_transcription_failed` codes. Only `transcriptionFailed` (503) is worth the same audio again.
 */
export type AiDictationRefusalReason = 'audioMissing' | 'audioUnsupported' | 'audioTooLarge' | 'audioTooLong' | 'languageInvalid' | 'transcriptionFailed';

const DICTATION_REFUSALS: Readonly<Record<string, AiDictationRefusalReason>> = {
  ai_audio_missing: 'audioMissing',
  ai_audio_unsupported: 'audioUnsupported',
  ai_audio_too_large: 'audioTooLarge',
  ai_audio_too_long: 'audioTooLong',
  ai_language_invalid: 'languageInvalid',
  ai_transcription_failed: 'transcriptionFailed',
};

/** Why a spreadsheet is not read: the backend's `ai_import_*` codes. */
export type AiImportRefusalReason =
  | 'xls'
  | 'link'
  | 'macros'
  | 'tooLarge'
  | 'tooManySheets'
  | 'unpackedTooLarge'
  | 'sheetTooLarge'
  | 'unreadable'
  | 'sheetsInvalid'
  | 'notKept'
  /** `ai_import_mode_required`: no chosen sheet names weekdays, and the import said no scheduling mode (R54). */
  | 'modeRequired';

const IMPORT_REFUSALS: Readonly<Record<string, AiImportRefusalReason>> = {
  ai_import_xls: 'xls',
  ai_import_link: 'link',
  ai_import_macros: 'macros',
  ai_import_too_large: 'tooLarge',
  ai_import_too_many_sheets: 'tooManySheets',
  ai_import_unpacked_too_large: 'unpackedTooLarge',
  ai_import_sheet_too_large: 'sheetTooLarge',
  ai_import_unreadable: 'unreadable',
  ai_import_sheets_invalid: 'sheetsInvalid',
  ai_import_not_kept: 'notKept',
  ai_import_mode_required: 'modeRequired',
};

export interface RefusalSource {
  readonly status: number;
  readonly code: string | undefined;
  /** The Problem Details body, whose extensions carry `window` and `resetsAt` on a 429. */
  readonly problem: object | undefined;
}

function extension(problem: object | undefined, name: string): string | null {
  if (problem === undefined || !(name in problem)) return null;
  const value: unknown = (problem as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : null;
}

export function aiRefusal(source: RefusalSource): AiRefusal {
  if (source.status === 403 && source.code === 'ai_unavailable') return { kind: 'unavailable' };
  if (source.status === 429 && source.code === 'ai_limit_reached') {
    const window: AiUsageWindow = extension(source.problem, 'window') === 'weekly' ? 'weekly' : 'fiveHour';
    return {
      kind: 'limit',
      meter: { percent: 100, window, resetsAt: extension(source.problem, 'resetsAt'), blocked: true },
    };
  }
  if (source.status === 409 && source.code === 'ai_turn_running') return { kind: 'stale' };
  if (source.status === 429) return { kind: 'busy' };
  // A decision on an exercise whose draft other writes kept moving under it (resolutions R8).
  if (source.status === 409 && source.code === 'ai_draft_stale') return { kind: 'stale' };
  if (source.status === 409 && source.code === 'ai_outline_not_waiting') return { kind: 'notWaiting' };
  if (source.status === 400 && source.code === 'ai_answer_invalid') return { kind: 'answerInvalid' };
  if (source.status === 400 && source.code === 'ai_snapshot_invalid') return { kind: 'snapshotInvalid' };
  // Any status: the body bound answers before the route with the same code.
  if (source.code === 'ai_snapshot_too_large') return { kind: 'snapshotTooLarge', limit: numberExtension(source.problem, 'limit') };
  if (source.status === 400 && source.code === 'ai_decision_invalid') return { kind: 'decisionInvalid' };
  // Any status: an upload over the transport's own bound is refused before the route runs, with the
  // same code and without a `limit`.
  const reason = source.code === undefined ? undefined : IMPORT_REFUSALS[source.code];
  if (reason !== undefined) {
    return { kind: 'import', reason, limit: numberExtension(source.problem, 'limit'), sheet: extension(source.problem, 'sheet') };
  }
  // Any status: an oversized body is refused by the transport before the route, with the same code.
  const dictation = source.code === undefined ? undefined : DICTATION_REFUSALS[source.code];
  if (dictation !== undefined) return { kind: 'dictation', reason: dictation };
  return { kind: 'failed' };
}

function numberExtension(problem: object | undefined, name: string): number | null {
  if (problem === undefined || !(name in problem)) return null;
  const value: unknown = (problem as Record<string, unknown>)[name];
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** One thing the save's own check found wrong with an edited draft: where, which rule, and the words. */
export interface AiDraftViolation {
  /** The place in the plan's wire shape, e.g. `phases[0].days[0].sessions[0].exercises[2].exerciseId`. */
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

/** Why an edit of a draft was not taken, and so what may happen next. */
export type AiDraftEditRefusal =
  /** 400 `ai_draft_edit_invalid`: the edited plan breaks the save's rules; nothing was stored. */
  | { readonly kind: 'invalid'; readonly violations: readonly AiDraftViolation[] }
  /** 409 `ai_draft_stale`: the draft moved on (another window, or an answer that was lost on the way). */
  | { readonly kind: 'stale' }
  /** 404 or 403: the conversation or the draft is not there, or not this trainer's, any more. */
  | { readonly kind: 'gone' }
  /** 429: too many edits too fast; send again after the server's own wait, when it named one. */
  | { readonly kind: 'tooMany'; readonly retryAfterSeconds: number | null }
  /** Any other 4xx: the request itself is refused, and sending it again would be refused again. */
  | { readonly kind: 'rejected' }
  /** No answer, a timeout (408) or the server's own failure: the edit is still here to be sent again. */
  | { readonly kind: 'failed' };

/** The refusal with the server's wait, when it named one: what `draftEditRefusal` reads. */
export interface DraftEditRefusalSource extends RefusalSource {
  readonly retryAfterSeconds: number | null;
}

export function draftEditRefusal(source: DraftEditRefusalSource): AiDraftEditRefusal {
  if (source.status === 409 && source.code === 'ai_draft_stale') return { kind: 'stale' };
  if (source.status === 400 && source.code === 'ai_draft_edit_invalid') {
    const raw: unknown =
      source.problem !== undefined && 'violations' in source.problem
        ? (source.problem as Record<string, unknown>).violations
        : undefined;
    const violations = (Array.isArray(raw) ? raw : []).flatMap((item: unknown): AiDraftViolation[] => {
      if (typeof item !== 'object' || item === null) return [];
      const { path, code, message } = item as Record<string, unknown>;
      return typeof code === 'string' || typeof message === 'string'
        ? [{ path: typeof path === 'string' ? path : '', code: typeof code === 'string' ? code : '', message: typeof message === 'string' ? message : '' }]
        : [];
    });
    return { kind: 'invalid', violations };
  }
  if (source.status === 404 || source.status === 403) return { kind: 'gone' };
  if (source.status === 429) return { kind: 'tooMany', retryAfterSeconds: source.retryAfterSeconds };
  if (source.status >= 400 && source.status < 500 && source.status !== 408) return { kind: 'rejected' };
  return { kind: 'failed' };
}

/**
 * How long to wait before asking about a running turn again, after `answers` answers. The film
 * watch's doubling (`processingPollDelayMs`), started lower and capped sooner: a clarifying question
 * arrives in seconds, and the build log is read while it grows (R12).
 */
export function turnPollDelayMs(answers: number): number {
  return Math.min(750 * 1.5 ** answers, 4_000);
}
